import React, { useId, useLayoutEffect, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";

export interface LoaderProps {
  isLoad?: boolean;
  open?: boolean;
  loading?: boolean;
  message?: string;
  className?: string;
}

// Global active loader tracker to prevent duplicate overlays when multiple components render <Loader />
const activeLoaderIds = new Set<string>();
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((listener) => listener());
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function registerLoader(id: string) {
  if (!activeLoaderIds.has(id)) {
    activeLoaderIds.add(id);
    notify();
  }
}

function unregisterLoader(id: string) {
  if (activeLoaderIds.delete(id)) {
    notify();
  }
}

function getFirstActiveId(): string | null {
  return activeLoaderIds.values().next().value || null;
}

const Loader: React.FC<LoaderProps> = (props) => {
  const instanceId = useId();

  // Support props.isLoad (primary convention), props.open, props.loading, or default to true when rendered conditionally without props
  const shouldShow =
    props.isLoad !== undefined
      ? Boolean(props.isLoad)
      : props.open !== undefined
      ? Boolean(props.open)
      : props.loading !== undefined
      ? Boolean(props.loading)
      : true;

  useLayoutEffect(() => {
    if (shouldShow) {
      registerLoader(instanceId);
      return () => {
        unregisterLoader(instanceId);
      };
    } else {
      unregisterLoader(instanceId);
    }
  }, [shouldShow, instanceId]);

  // Subscribe to external store: only the first active loader instance renders the DOM overlay
  const isPrimary = useSyncExternalStore(
    subscribe,
    () => shouldShow && (activeLoaderIds.size === 0 || getFirstActiveId() === instanceId),
    () => false
  );

  if (!shouldShow || !isPrimary) {
    return null;
  }

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className={`global_loader_overlay ${props.className || ""}`.trim()}
      id="global-screen-loader"
      role="status"
      aria-live="polite"
      aria-label="Loading"
    >
      <div className="loader_loading">
        <div className="loader_spinner" />
        {props.message && <p className="loader_text">{props.message}</p>}
      </div>
    </div>,
    document.body
  );
};

export default Loader;
